import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from "@angular/core";
import { DeviceDetectorService } from "ngx-device-detector";
import { GenericListingService } from "../../services/generic-listing.service";
import { AssigneeRole } from "../../models/main-filter-view";
import { PodsDropdownComponent } from "../pods-dropdown/pods-dropdown.component";
import { ColumnMapping, DataFilter } from "../dynamic-table/dynamic-table.component";
import { Router } from "@angular/router";
import { iterateSortDirectionDataFilter } from "src/app/utils/tab-utils";
import { AuthService } from "@core/auth/auth.service";
import { Nullable } from "primeng/ts-helpers";
import { DCCompanyType } from "src/app/services/companies/companies.service";
import { Pod } from "@core/models/pod-filter";
import { firmPod } from "@core/static/tabs";
import { TabUtilsService } from "src/app/services/tab-utils.service";
import { Subscription } from "rxjs";

export enum HorizontalBarType {
  None = "none",
  Stage = "stage",
  Interaction = "interaction",
  Timeline = "timeline",
}

enum FilterType {
  Bar = "bar",
  Pod = "pod",
  Search = "search",
  DcCompany = "dcCompany",
}

export interface SubRoute {
  label: string;
  route: string;
}

@Component({
  selector: "bpc-generic-listing",
  templateUrl: "./generic-listing.component.html",
  styleUrl: "./generic-listing.component.scss",
})
export class GenericListingComponent implements OnInit {

  HorizontalBarType = HorizontalBarType;
  FilterType = FilterType;
  @ViewChild("podsDropdown") podsDropdown!: PodsDropdownComponent;
  @ViewChild('searchInput') searchInput!: ElementRef;
  searchQuery: Nullable<string> = null;

  @Input() isLoading = false;
  @Input() title = "Listing";
  @Input() hasPod = false;
  @Input() barType = HorizontalBarType.None;
  @Input() hideTable = false;
  @Input() enableSelection = false; // Enables checkbox selection

  @Input() tableClass!: Nullable<string>;
  @Input() shouldShowPagination: boolean = true;
  
  currentRoute = "";
  hasPodSelected = false;
  barLoading: boolean = true;
  barData: any = [];
  selectedTypes: DCCompanyType[] = [];
  activePod!: Pod;
  pageSize: number = 100;

  @Input() columns: ColumnMapping[] = [];
  @Input() data = [];
  @Input() totalCount = 0;
  @Input() companiesFilter!: DataFilter;
  @Input() hasFilters: boolean = true;

  @Output() onFilterChange = new EventEmitter<{}>();
  @Output() onLoadMore = new EventEmitter<boolean>();
  @Output() onSelectionChange = new EventEmitter<any[]>(); // Emits when checkbox selection changes

  clearFiltersSubscription!: Subscription;
  refreshDataSubscription!: Subscription;

  constructor(
    private route: Router,
    private deviceDetectorService: DeviceDetectorService,
    private genericListingService: GenericListingService,
    private authService: AuthService,
    private tabsUtilsService : TabUtilsService,
  ) {
    this.route.events.subscribe(() => {
      this.currentRoute = this.route.url;
    });
  }

  ngOnInit(): void {
    this.clearFiltersSubscription = this.tabsUtilsService.clearFilters.subscribe(()=>{
      this.activePod = firmPod;
      this.selectedTypes = [];
      this.pageSize = 100;
      this.fetchBarData([]);
    })

    this.refreshDataSubscription = this.tabsUtilsService.refreshData.subscribe(() =>{
      this.fetchBarData(this.activePod?.members!.map(member => member.userId));
    })  
  }



  fetchBarData(ids: number[] | undefined) {
    if (this.barType !== HorizontalBarType.None) {
      this.barLoading = true;
      this.genericListingService.fetchBarData(this.barType, ids, this.hideTable ? this.selectedTypes : undefined).subscribe((data) => {
        if (data.length > 0){
          this.barData = data;
          this.barLoading = false;
        }
      });
    }
  }


  
  get isMobile(): boolean {
    return this.deviceDetectorService.isMobile();
  }
  

  filterData(e: any, type: string) {
    const rawValueMapping: any = {};
    const filters: any = {};

    
    if (type === FilterType.Bar) {
      if (this.barType === HorizontalBarType.Stage) {
        if (Array.isArray(e.value.id)){
          filters.statusesInclude = [...e.value.id]
        }else{
          filters.statusesInclude = e.value.id !== undefined ? [e.value.id] : undefined;
        }
        rawValueMapping["statusesInclude"] = filters.statusesInclude;
      } else if (this.barType === HorizontalBarType.Interaction) {
        filters.interactionTypes = e.value.id !== undefined ? [e.value.id, -1] : undefined;
        rawValueMapping["interactionTypes"] = filters.interactionTypes;
        //filters.rawValueMapping =
        //('{"dcCompanyTypes":[{"id":6,"name":"Target"}],"interactionTypes":[{"id":0,"name":"Notes"}]}');
      }
      else if (this.barType === HorizontalBarType.Timeline) {
        filters.statusChangeFromDate  = e.value.fromDate;
        filters.statusChangeToDate  = e.value.toDate;
      }
    } else if (type === FilterType.Pod) {
      this.activePod = e;
      const includeString =
        this.barType === HorizontalBarType.Stage ? "assigneesInclude" : "interactionInitiatorsInclude";
      filters[includeString] = e.members.map((m: any) => m.userId);
      rawValueMapping[includeString] = filters[includeString];
      filters.assigneeRole = AssigneeRole.COVERAGE;

      if (filters[includeString]?.length === 0) {
        if (e.name == "My Companies"){
          filters[includeString] = [this.authService.userId]
          this.fetchBarData(filters[includeString]);
        }
        else{
          filters[includeString] = undefined;
          this.fetchBarData(undefined);
        }
      } else {
        this.fetchBarData(filters[includeString]);
      }
    }
    else if (type === FilterType.DcCompany){
      const dcCompanyTypes = e;
      if (dcCompanyTypes.length > 0) {
        filters.dcCompanyTypes = dcCompanyTypes;
      } else {
        delete filters.dcCompanyTypes;
      }
      this.selectedTypes = dcCompanyTypes;
      this.fetchBarData(this.activePod?.members!.map(member => member.userId));
      this.onFilterChange.emit(filters);
    }
    else if (type === FilterType.Search){
      if (e.target.value != ""){

        const companyName = [e.target.value]

        filters.companyName = companyName;
      }
      else{
        filters.companyName = null;
        this.searchQuery = '';
      }

      this.searchInput.nativeElement.blur(); 
    }

    this.onFilterChange.emit(filters);
  }

  loadMoreData() {
    this.onLoadMore.emit(true);
  }

  toggleFilter() {
    this.hasPodSelected = !this.hasPodSelected;
    if (!this.hasPodSelected) {
      this.onFilterChange.emit({
        assigneesInclude: undefined,
        interactionInitiatorsInclude: undefined,
      });
      this.fetchBarData(undefined);
      this.podsDropdown.resetActivePod();
    }
  }

  onPageChange(pageNumber: number) {
    this.onFilterChange.emit({ pageNumber });
  }

  onPageSizeChange(pageSize: number) {
    this.onFilterChange.emit({ pageNumber: 1, pageSize });
  }

  onColumnSortClick(column: ColumnMapping) {
    const newFilter = iterateSortDirectionDataFilter(this.companiesFilter, column);
    this.onFilterChange.emit(newFilter);
  }

  onCheckboxSelectionChange(selectedRows: any[]) {
    this.onSelectionChange.emit(selectedRows);
  }

  ngOnDestroy(){
    if (this.clearFiltersSubscription){
      this.clearFiltersSubscription.unsubscribe();
    }
    if (this.refreshDataSubscription){
      this.refreshDataSubscription.unsubscribe();
    }
  }
}
